import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Check, Sparkles, Wallet as WalletIcon } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS = ["New", "Contacted", "Qualified", "Won", "Lost"];
const statusCls = {
  New: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  Contacted: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  Qualified: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  Won: "bg-green-500/10 text-green-400 border-green-500/20",
  Lost: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function LeadsPortal() {
  const { profile, wallet, refreshWallet } = useApp();
  const [leads, setLeads] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", source: "Manual", status: "New" });

  const load = () => api.getLeads(profile.id).then(setLeads).catch(() => {});
  useEffect(() => { if (profile) load(); }, [profile]);

  const offers = leads.filter((l) => l.offer_status === "offered");
  const myLeads = leads.filter((l) => l.offer_status !== "offered" && l.offer_status !== "declined");

  const accept = async (l) => {
    try {
      await api.acceptLead(l.id);
      await refreshWallet();
      toast.success(`Lead accepted${l.price ? ` — $${l.price} charged to wallet` : ""}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept lead.");
    }
  };

  const decline = async (l) => {
    await api.declineLead(l.id);
    toast.info("Lead declined.");
    load();
  };

  const addLead = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    await api.createLead({ profile_id: profile.id, ...form });
    toast.success("Lead added");
    setForm({ name: "", business: "", email: "", phone: "", source: "Manual", status: "New" });
    setShowAdd(false);
    load();
  };

  const changeStatus = async (id, status) => {
    await api.updateLead(id, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  const removeLead = async (id) => {
    await api.deleteLead(id);
    toast.success("Lead removed");
    load();
  };

  return (
    <div className="h-full overflow-y-auto p-6 md:p-10 pb-24 relative hide-scrollbar">
      <div className="max-w-6xl mx-auto space-y-8 fade-in relative z-10">
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
          <div>
            <h2 className="text-3xl font-bold text-white tracking-tight font-display">Leads Portal</h2>
            <p className="text-sm text-zinc-400 mt-1">Review leads we've sourced for you, then accept or decline. Manage your own contacts too.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-4 py-2 rounded-xl bg-black/40 border border-white/10 flex items-center gap-2 text-sm">
              <WalletIcon size={15} className="text-green-400" />
              <span className="text-zinc-400">Balance</span>
              <span className="text-white font-bold">${(wallet?.balance ?? 0).toLocaleString()}</span>
            </div>
            <button onClick={() => setShowAdd(true)} className="px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition glow-box flex items-center border border-primary-400/50"><Plus size={16} className="mr-2" /> Add Lead</button>
          </div>
        </div>

        {/* Lead offers */}
        {offers.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={18} className="text-orange-400" />
              <h3 className="text-lg font-bold text-white">Available Lead Offers</h3>
              <span className="px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20 text-xs font-bold">{offers.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {offers.map((l) => (
                <div key={l.id} className="glass-panel rounded-2xl p-6 border-orange-500/20">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="font-bold text-white text-lg">{l.name}</div>
                      <div className="text-sm text-zinc-500">{l.business}</div>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-orange-500/10 text-orange-400 border border-orange-500/30">{l.source}</span>
                  </div>
                  <div className="text-xs text-zinc-400 space-y-1 mb-5">
                    {l.email && <div><span className="text-zinc-600">Email:</span> {l.email}</div>}
                    {l.phone && <div><span className="text-zinc-600">Phone:</span> {l.phone}</div>}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div>
                      <span className="text-xs text-zinc-500">Lead price</span>
                      <div className="text-2xl font-extrabold text-white">{l.price ? `$${l.price}` : "Free"}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => decline(l)} className="px-4 py-2.5 rounded-lg border border-white/10 text-zinc-300 hover:bg-white/5 text-sm font-bold transition-colors">Decline</button>
                      <button onClick={() => accept(l)} className="px-4 py-2.5 rounded-lg bg-green-600 text-white hover:bg-green-500 text-sm font-bold transition-colors flex items-center"><Check size={15} className="mr-1.5" /> Accept</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My pipeline */}
        <div>
          <h3 className="text-lg font-bold text-white mb-4">My Leads</h3>
          <div className="glass-panel rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-zinc-300">
                <thead className="bg-white/5 text-zinc-400 uppercase text-xs font-bold tracking-wider border-b border-white/10">
                  <tr>
                    <th className="px-6 py-5">Name / Business</th>
                    <th className="px-6 py-5">Contact Info</th>
                    <th className="px-6 py-5">Source</th>
                    <th className="px-6 py-5">Status</th>
                    <th className="px-6 py-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 bg-black/20">
                  {myLeads.length === 0 && (<tr><td colSpan={5} className="px-6 py-12 text-center text-zinc-500">No leads yet. Accept an offer above or add your own.</td></tr>)}
                  {myLeads.map((l) => (
                    <tr key={l.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4"><div className="font-bold text-white">{l.name}</div><div className="text-xs text-zinc-500">{l.business}</div></td>
                      <td className="px-6 py-4"><div className="text-zinc-300">{l.email}</div><div className="text-xs text-zinc-500">{l.phone}</div></td>
                      <td className="px-6 py-4"><span className="text-zinc-400">{l.source}</span></td>
                      <td className="px-6 py-4">
                        <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                          <SelectTrigger className={`w-32 h-8 text-xs font-bold rounded-md border ${statusCls[l.status] || statusCls.New}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10 text-white">
                            {STATUS.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white text-xs">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4 text-right"><button onClick={() => removeLead(l.id)} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
          <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-md relative fade-in overflow-hidden border border-white/10">
            <div className="px-6 py-5 border-b border-white/10 flex justify-between items-center bg-white/5">
              <h3 className="text-lg font-bold text-white">Add New Lead</h3>
              <button onClick={() => setShowAdd(false)} className="text-zinc-500 hover:text-white"><X /></button>
            </div>
            <form onSubmit={addLead} className="p-8 space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name *" className="w-full px-5 py-3.5 rounded-xl dark-input" />
              <input value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} placeholder="Business Name" className="w-full px-5 py-3.5 rounded-xl dark-input" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full px-5 py-3.5 rounded-xl dark-input" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="w-full px-5 py-3.5 rounded-xl dark-input" />
              <button type="submit" className="w-full py-3.5 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-500 transition-colors shadow-[0_0_15px_rgba(59,130,246,0.4)]">Add Lead</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
